/**
 * Session detail tab (reports).
 */
import * as React from 'react';
import type { Session } from '@/types/remote/session.types';
import type { ReportDetail } from '@/types/remote/report.types';
import {
  REMOTE_REPORT_STATUS_CONFIG,
  REMOTE_REPORT_SCORING_STATUS_CONFIG,
  REMOTE_REPORT_SESSION_TYPE_CONFIG,
  REPORT_REMOTE_SCHEMA,
  SCORING_TARGET_LABELS,
} from '@/components/reports/report-configs';
import {
  SESSION_REMOTE_REPORT_AVAILABILITY_MESSAGE,
  SESSION_REMOTE_REPORT_EMPTY_MESSAGE,
} from '@/components/sessions/session-configs';
import { ReportFields, ScoringContent } from '@/components/reports/remote/renderReportDetails';
import { reportIcon } from '@/components/reports/remote/report-table-utils';
import { DetailsCard } from '@/components/common/DetailsCard';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Star, ClipboardList } from 'lucide-react';

/** Props for {@link SessionReportsTab}. */
interface SessionReportsTabProps {
  session: Session;
  reportDetail: ReportDetail | undefined;
  reportLoading: boolean;
  reportNotFound: boolean;
}

/** Session detail tab (reports). */
export const SessionReportsTab: React.FC<SessionReportsTabProps> = ({
  session,
  reportDetail,
  reportLoading,
  reportNotFound,
}) => {
  if (reportLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-1-nuance-1)]" />
        <p className="text-sm text-muted-foreground">Chargement du compte rendu…</p>
      </div>
    );
  }

  const report = reportDetail?.reportView;
  const scoring = reportDetail?.scoringView;

  if (reportNotFound || !report) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-center text-gray-400">
        <ClipboardList className="w-10 h-10 opacity-30" />
        <p className="text-sm font-medium text-gray-500">
          {SESSION_REMOTE_REPORT_EMPTY_MESSAGE[session.type] ?? 'Pas encore de compte rendu'}
        </p>
        <p className="text-xs text-gray-400">
          {SESSION_REMOTE_REPORT_AVAILABILITY_MESSAGE[session.status] ?? ''}
        </p>
      </div>
    );
  }

  const reportStatusCfg = REMOTE_REPORT_STATUS_CONFIG[report.status];
  const scoringStatusCfg = scoring?.status
    ? REMOTE_REPORT_SCORING_STATUS_CONFIG[scoring.status]
    : undefined;
  const scoringEntries = scoring?.entries ?? [];
  const typeLabel = REMOTE_REPORT_SESSION_TYPE_CONFIG[session.type]?.label ?? session.type;
  let reportSectionTitle = 'Compte rendu';
  if (report.schemaId === REPORT_REMOTE_SCHEMA.tutoring) {
    reportSectionTitle = `Compte rendu - ${typeLabel}`;
  } else if (
    report.schemaId === REPORT_REMOTE_SCHEMA.synthesisInformationMeeting ||
    report.schemaId === REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting
  ) {
    reportSectionTitle = `Synthèse - ${typeLabel}`;
  }

  return (
    <div className="details-view-grid">
      <div className="col-span-full flex items-center gap-2 flex-wrap pb-2 border-b border-border">
        {reportStatusCfg && (
          <LabelBadge
            label={reportStatusCfg.label}
            icon={reportStatusCfg.icon}
            variant={reportStatusCfg.badgeVariant}
            color={reportStatusCfg.badgeColor}
            size="sm"
          />
        )}
        {scoringStatusCfg && (
          <LabelBadge
            label={scoringStatusCfg.label}
            icon={scoringStatusCfg.icon}
            variant={scoringStatusCfg.badgeVariant}
            color={scoringStatusCfg.badgeColor}
            size="sm"
          />
        )}
        {report.updatedAt && (
          <span className="text-xs text-muted-foreground ml-auto">
            Mis à jour le{' '}
            {new Date(report.updatedAt).toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      <DetailsCard
        icon={reportIcon(report.schemaId)}
        title={reportSectionTitle}
        colorName="color-1"
        colSpan="full"
      >
        <ReportFields schemaId={report.schemaId} fields={report.fields} />
      </DetailsCard>

      {scoring && scoringEntries.length > 0 ? (
        scoringEntries.map((entry) => {
          let scoringCardTitle = 'Scoring';
          if (scoring.schemaId === 'scoring_eleve') {
            scoringCardTitle =
              entry.targetRole === 'student'
                ? 'Scoring élève'
                : `Scoring élève — ${SCORING_TARGET_LABELS[entry.targetRole] ?? entry.targetRole}`;
          } else if (
            scoring.schemaId === 'scoring_light_ri' ||
            scoring.schemaId === 'scoring_light_ra'
          ) {
            scoringCardTitle = 'Scoring light';
          }
          return (
            <DetailsCard
              key={entry.id}
              icon={Star}
              title={scoringCardTitle}
              colorName="color-3"
              colSpan={1}
            >
              <ScoringContent schemaId={scoring.schemaId} fields={entry.fields} />
            </DetailsCard>
          );
        })
      ) : (
        <DetailsCard icon={Star} title="Scoring" colorName="color-3" colSpan="full">
          <p className="text-sm text-muted-foreground italic">Scoring non encore renseigné</p>
        </DetailsCard>
      )}
    </div>
  );
};
